import { BaseView, PromiseCompletionSource } from "scene-manager";
import { ICanvasScaler } from "../../../../canvas/ICanvasScaler";
import { IDestroyOptions } from "@pixi/display";
import { IFocusable } from "../../../layers/IFocusable";
import { CommonUIResourcesPackage } from "../../common/resources/CommonUIResourcesPackage";
import { InfoResourcesPackage } from "../resources/InfoResourcesPackage";
import { NineSlicePlane } from "@pixi/mesh-extras";
import { TextStyle } from "@pixi/text";
import { getDefaultFontFamily } from "../../common/TextUtils";
import { ScaleUpButton } from "../../common/components/buttons/ScaleUpButton";
import { Localization } from "../../common/Localization";
import { FitText } from "../../common/components/FitText";
import { AudioManager } from "../../../audio/AudioManager";
import { DotsIndicator } from "../components/DotsIndicator";
import { HowToPlayPage } from "../components/page/HowToPlayPage";
import { SpecBlockAndMoreScorePage } from "../components/page/SpecBlockAndMoreScorePage";
import { IPage } from "../components/page/IPage";
import { InfoModel } from "../model/InfoModel";
import { InfoController } from "../controller/InfoController";

export class InfoView extends BaseView<void> implements IFocusable {
    private root: NineSlicePlane;
    private infoTitleText: FitText;
    private closeButton: ScaleUpButton;
    private howToPlayPage: HowToPlayPage;
    private specBlockAndMoreScorePage: SpecBlockAndMoreScorePage;
    private nextPageButton: ScaleUpButton;
    private previousPageButton: ScaleUpButton;
    private dotsIndicator: DotsIndicator;
    private pages: Array<IPage>;

    public readonly focusable: boolean = true;

    public constructor(
        private readonly infoModel: InfoModel,
        private readonly infoController: InfoController,
        private readonly commonResources: CommonUIResourcesPackage,
        private readonly infoResources: InfoResourcesPackage,
        private readonly canvasScaler: ICanvasScaler,
        private readonly localization: Localization,
        private readonly audioManager: AudioManager,
        operationControls: PromiseCompletionSource<void>
    ) {
        super(operationControls);
        this.build();
        this.layout();
        this.pages = new Array<IPage>(this.howToPlayPage, this.specBlockAndMoreScorePage);
        this.infoModel.onChange.add(this.updatePages, this);
        this.updatePages();
    }

    public override start(): void {
    }

    private build(): void {
        this.root = new NineSlicePlane(this.commonResources.assets_common_root_png, 105, 150, 105, 150);
        this.root.width = 800;
        this.root.height = 1060;
        this.addChild(this.root);
        this.infoTitleText = new FitText(this.localization.getText("info_title"), this.getInfoTextStyle(55), 250, 100);
        this.root.addChild(this.infoTitleText);
        this.howToPlayPage = new HowToPlayPage(this.infoResources, this.localization);
        this.root.addChild(this.howToPlayPage);
        this.specBlockAndMoreScorePage = new SpecBlockAndMoreScorePage(this.infoResources, this.localization);
        this.root.addChild(this.specBlockAndMoreScorePage);
        this.closeButton = new ScaleUpButton({ defaultTexture: this.commonResources.assets_common_close_button_png }, this.audioManager);
        this.root.addChild(this.closeButton);
        this.closeButton.onUp.add(this.handleCloseButtonClick, this);
        this.previousPageButton = new ScaleUpButton({ defaultTexture: this.infoResources.assets_info_arrow_left_png }, this.audioManager);
        this.root.addChild(this.previousPageButton);
        this.previousPageButton.onUp.add(this.handlePreviousPageButtonClick, this);
        this.nextPageButton = new ScaleUpButton({ defaultTexture: this.infoResources.assets_info_arrow_right_png }, this.audioManager);
        this.root.addChild(this.nextPageButton);
        this.nextPageButton.onUp.add(this.handleNextPageButtonClick, this);
        this.dotsIndicator = new DotsIndicator(this.infoResources.assets_info_actived_dot_png, this.infoResources.assets_info_dot_png, this.infoModel.TotalPages);
        this.root.addChild(this.dotsIndicator);
    }

    private layout(): void {
        const referenceWidth = this.canvasScaler.referenceWidth;
        this.root.position.set((referenceWidth - this.root.width) * 0.5, 251);
        this.infoTitleText.position.set((this.root.width - this.infoTitleText.width) * 0.5, 22);
        this.closeButton.position.set(720, 40);
        this.previousPageButton.position.set(25, (this.root.height - this.previousPageButton.height) * 0.5);
        this.nextPageButton.position.set(this.root.width - this.nextPageButton.width - 25, (this.root.height - this.nextPageButton.height) * 0.5);
        this.dotsIndicator.position.set((this.root.width - this.dotsIndicator.width) * 0.5, 975);
        this.howToPlayPage.position.set((this.root.width - this.howToPlayPage.width) * 0.5, 182);
        this.specBlockAndMoreScorePage.position.set((this.root.width - this.specBlockAndMoreScorePage.width) * 0.5, 170);
    }

    private updatePages(): void {
        const previousPage = this.infoModel.PreviousPageIndex;
        const currentPage = this.infoModel.CurrentPageIndex;
        this.dotsIndicator.update(currentPage, previousPage);
        this.previousPageButton.visible = !this.infoModel.isFirstPage();
        this.nextPageButton.visible = !this.infoModel.isLastPage();
        this.pages[previousPage].hide();
        this.pages[currentPage].show();
    }

    private handleNextPageButtonClick(): void {
        this.infoController.nextPage();
    }

    private handlePreviousPageButtonClick(): void {
        this.infoController.previousPage();
    }

    private handleCloseButtonClick(): void {
        this.operationControls.complete();
    }

    private getInfoTextStyle(size: number): TextStyle {
        return new TextStyle({
            fontFamily: getDefaultFontFamily(),
            fontSize: size,
            fill: "#ffffff",
            stroke: "#006A5D",
            strokeThickness: 6,
            align: "center"
        });
    }

    public override destroy(options?: IDestroyOptions | boolean): void {
        this.previousPageButton.onUp.remove(this.handlePreviousPageButtonClick, this);
        this.nextPageButton.onUp.remove(this.handleNextPageButtonClick, this);
        this.infoModel.onChange.remove(this.updatePages, this);
        this.closeButton.onUp.remove(this.handleCloseButtonClick, this);
        super.destroy(options);
    }
}